import React, { useEffect, useState } from "react";
import { listTeamInvites, createTeamInvite, revokeTeamInvite } from "@/services/account.service";
import { updateWorkspace } from "@/services/auth.service";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { Section } from "./SettingsShared";

const TeamTab = ({ user, refreshUser }) => {
  const [workspace, setWorkspace] = useState(user.workspace_name || "My workspace");
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Member");

  const load = async () => { try { setInvites(await listTeamInvites()); } catch {} };
  useEffect(() => { load(); }, []);

  const saveWs = async () => {
    try { await updateWorkspace({ workspace_name: workspace }); toast.success("Workspace saved"); await refreshUser(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addInvite = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    try { await createTeamInvite({ email, role }); toast.success("Invite sent"); setEmail(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revoke = async (id) => {
    try { await revokeTeamInvite(id); toast.success("Invite revoked"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <Section title="Workspace" description="Your workspace name appears on invites and emails." data-testid="team-workspace-section">
        <div className="flex gap-2">
          <Input value={workspace} onChange={(e) => setWorkspace(e.target.value)} className="h-11" data-testid="workspace-input" />
          <Button onClick={saveWs} data-testid="workspace-save-btn">Save</Button>
        </div>
      </Section>
      <Section title="Invite teammates" description="Add collaborators to your PAYD workspace." data-testid="team-invites-section">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-0">
            <Label>Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@acme.com" className="mt-1.5 h-11" data-testid="invite-email-input" />
          </div>
          <div>
            <Label>Role</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1.5 h-11 rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="invite-role-select">
              <option value="Admin">Admin</option>
              <option value="Member">Member</option>
            </select>
          </div>
          <Button onClick={addInvite} data-testid="send-invite-btn">Send invite</Button>
        </div>
        <div className="space-y-2">
          {invites.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="invites-empty">No pending invites.</div>
          ) : invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between border border-border rounded-lg p-3" data-testid={`invite-row-${inv.id}`}>
              <div>
                <div className="font-medium">{inv.invitee_email}</div>
                <div className="text-xs text-muted-foreground">{inv.role} · {inv.status} · {inv.created_at}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => revoke(inv.id)} data-testid={`revoke-${inv.id}-btn`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
};

export default TeamTab;
